'use client';

import { applyTransformChain } from '@capture/shared';
import type { TransformPluginRow } from '@/features/admin/transform-plugins/api/transform-plugins-client';
import styles from './TransformPreviewPane.module.css';

interface ChainStep {
  type: string;
  config: Record<string, unknown>;
}

interface Props {
  seed: string;
  chain: ChainStep[];
  /** Optional context — sample values for cross-field ops. */
  contextFields?: Record<string, unknown>;
  /**
   * F-043 — published tenant transform plugins. A chain step referencing one is
   * unknown to the static built-in vocab (`applyTransformChain` passes it through
   * and reports "Unknown op"), but it is a real, resolvable op at export time.
   * Supplying the plugin set lets the preview label such steps honestly (plugin
   * label + "custom" badge, "applied at export") instead of flagging them as
   * dead ops. The plugin's own transform is NOT run locally: its DSL evaluator
   * needs a tenant host context (and many plugins declare host capabilities like
   * suite lookups) that the browser can't authentically provide, so a labeled
   * "applied at export" placeholder is the honest preview.
   */
  plugins?: TransformPluginRow[];
}

export function TransformPreviewPane({ seed, chain, contextFields, plugins = [] }: Props) {
  const result = applyTransformChain(
    seed,
    chain,
    { allFieldValues: contextFields ?? {} },
    { withTrace: true, withErrors: true },
  );

  // F-043 — resolve step ids that are actually published plugins (mirrors the
  // picker-merge pattern) so they read as real custom ops, not dead ones.
  const pluginById = new Map(plugins.map((p) => [p.pluginId, p]));

  // Genuinely-unknown ops = the withErrors set minus anything a plugin resolves.
  const unknownErrors = (result.errors ?? []).filter((e) => !pluginById.has(e.opId));

  return (
    <div className={styles.pane}>
      <div className={styles.seed}>
        <span className={styles.seedLabel}>Input:</span>
        <span className={styles.seedValue}>{seed || '(empty)'}</span>
      </div>
      {(result.trace ?? []).map((entry) => {
        const plugin = pluginById.get(entry.op);
        // A plugin step is not an error, even though the built-in evaluator
        // passed it through with an "Unknown op" note — clear that styling.
        const isError = entry.error != null && plugin == null;
        return (
          <div
            key={entry.step}
            className={`${styles.step} ${isError ? styles.stepError : ''}`}
            data-testid="preview-step"
          >
            <span className={styles.arrow}>→</span>
            <span className={styles.opName}>{plugin ? plugin.label : entry.op}</span>
            {plugin ? (
              <>
                <span className={styles.pluginBadge}>custom</span>
                <span className={styles.pluginNote}>applied at export</span>
              </>
            ) : (
              <span className={styles.output}>{String(entry.output)}</span>
            )}
            {isError && <span className={styles.errorBadge}>{entry.error}</span>}
          </div>
        );
      })}
      {unknownErrors.length > 0 && (
        // role="alert" + aria-live="polite": polite overrides alert's default
        // assertive so screen readers don't re-announce on every config edit.
        <div className={styles.errorBanner} role="alert" aria-live="polite">
          <strong>
            {unknownErrors.length === 1 ? '1 unknown step' : `${unknownErrors.length} unknown steps`}
          </strong>
          {' — silently passed through. Stored chains may reference ops that no longer exist.'}
          <ul className={styles.errorList}>
            {unknownErrors.map((e) => (
              <li key={`${e.stepIndex}-${e.opId}`}>
                Step {e.stepIndex + 1} (<code>{e.opId}</code>): {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
